import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, ShieldAlert, BarChart3, Users, Heart, MessageCircle,
  Calendar, ShieldCheck, TrendingDown, Trophy, MapPin, Cake, RotateCcw, Bell,
} from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { labelForConnection } from "@/data/connections";
import { toast } from "sonner";

const WINDOWS = [
  { id: "7d", label: "Last 7 days" },
  { id: "30d", label: "Last 30 days" },
  { id: "all", label: "All time" },
];

const pct = (n, base) => (base > 0 ? Math.round((n / base) * 1000) / 10 : 0);

export default function AdminAnalytics() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState("all");
  const [resetting, setResetting] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/analytics", { params: { window: range } });
      setData(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not load analytics");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [range]);

  const setBaseline = async (clear) => {
    setResetting(true);
    try {
      await api.post("/admin/analytics/baseline", { clear });
      toast.success(clear ? "Baseline cleared — showing all-time again" : "Baseline reset — analytics start from now");
      setConfirmReset(false);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Reset failed");
    } finally { setResetting(false); }
  };

  if (user?.role !== "admin") {
    return (
      <div className="min-h-screen bg-footy-bg text-white flex items-center justify-center px-6">
        <div className="footy-card p-8 text-center max-w-sm">
          <ShieldAlert className="mx-auto text-footy-red mb-3" />
          <h3 className="font-display text-2xl uppercase">Admin only</h3>
          <Link to="/discover" className="footy-btn-primary mt-6 inline-flex">Back to app</Link>
        </div>
      </div>
    );
  }

  const funnel = data?.funnel || [];
  const engagement = data?.engagement || {};
  const conn = data?.connection_types || [];
  const clubs = data?.top_clubs || [];
  const nudge = data?.notification_nudge || { matches: { shown: 0, subscribed: 0, dismissed: 0 }, chat: { shown: 0, subscribed: 0, dismissed: 0 }, fixtures: { shown: 0, subscribed: 0, dismissed: 0 } };
  const total = funnel[0]?.count || 0;

  return (
    <div className="min-h-screen bg-footy-bg text-white grain">
      <header className="sticky top-0 z-30 bg-footy-bg/90 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-4xl mx-auto flex items-center justify-between px-5 py-4">
          <div className="flex items-center gap-3">
            <Link to="/admin/reports" className="p-2 -ml-2 rounded-full hover:bg-white/10" data-testid="analytics-back"><ArrowLeft size={20} /></Link>
            <h1 className="font-display text-3xl uppercase tracking-wider">Analytics</h1>
          </div>
          <span className="text-xs uppercase tracking-widest text-footy-red font-bold">Admin</span>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-5 pt-6 pb-24">
        {/* Time-window filter */}
        <div className="flex items-center gap-2 flex-wrap justify-between" data-testid="analytics-window-filter">
          <div className="flex items-center gap-2 flex-wrap">
            {WINDOWS.map(w => (
              <button key={w.id} onClick={()=>setRange(w.id)} data-testid={`analytics-window-${w.id}`}
                className={`text-xs font-semibold uppercase tracking-widest px-3 py-1.5 rounded-full border transition-colors ${
                  range === w.id ? "bg-footy-red border-footy-red text-white"
                  : "bg-white/5 border-white/15 text-slate-300 hover:bg-white/10"}`}>
                {w.label}
              </button>
            ))}
          </div>
          <button onClick={()=>setConfirmReset(true)} disabled={resetting}
            className="text-xs uppercase tracking-widest text-slate-400 hover:text-white border border-white/10 hover:border-white/30 rounded-full px-3 py-1.5 inline-flex items-center gap-1.5"
            data-testid="analytics-reset-baseline"
            title="Ignore historic data — analytics restart from now">
            <RotateCcw size={12} /> Reset baseline
          </button>
        </div>

        {/* Baseline banner */}
        {data?.baseline_at && (
          <div className="mt-3 flex items-center justify-between gap-3 px-4 py-2.5 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-xs" data-testid="analytics-baseline-banner">
            <span className="text-emerald-200">
              <span className="font-bold uppercase tracking-widest text-emerald-400">Baseline active</span>
              {" — data from "}
              <span className="font-semibold">{new Date(data.baseline_at).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</span>
              {" onwards. Historic data still in the database, just hidden here."}
            </span>
            <button onClick={()=>setBaseline(true)} disabled={resetting}
              className="text-emerald-300 hover:text-white underline underline-offset-2 flex-none"
              data-testid="analytics-clear-baseline">
              Show all-time
            </button>
          </div>
        )}

        {loading ? (
          <div className="mt-6 space-y-3">{[1,2,3].map(i => <div key={i} className="h-32 rounded-2xl bg-white/5 animate-pulse" />)}</div>
        ) : !data ? null : (
          <>
            {/* Top-line stats */}
            <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="analytics-toplines">
              <Stat label="Signups" value={data.signups} icon={Users} />
              <Stat label="Completed onboarding" value={funnel.find(f => f.key === "onboarding_ok")?.count ?? 0} icon={Trophy} tone="emerald" />
              <Stat label="Photos uploaded" value={funnel.find(f => f.key === "photo")?.count ?? 0} icon={ShieldCheck} />
              <Stat label="Photos verified" value={funnel.find(f => f.key === "verified")?.count ?? 0} icon={ShieldCheck} tone="emerald" />
            </div>

            {/* Signup funnel */}
            <section className="mt-8" data-testid="analytics-funnel">
              <div className="flex items-center gap-2 mb-3">
                <BarChart3 size={16} className="text-footy-red" />
                <h2 className="font-display text-2xl uppercase leading-none">Signup funnel</h2>
              </div>
              <p className="text-xs text-slate-500 mb-4">
                How many people made it past each step. Big drops here are your next problem to solve.
                <span className="block mt-1 text-slate-600">Step-level counts only include users who signed up after step tracking shipped — the completed-profile total is always accurate.</span>
              </p>
              <div className="footy-card p-5">
                {funnel.map((f, i) => {
                  const width = total > 0 ? Math.max(4, (f.count / total) * 100) : 0;
                  const fromPrev = i > 0 ? funnel[i - 1].count : total;
                  const stepPct = pct(f.count, fromPrev);
                  const dropped = fromPrev - f.count;
                  const isBigDrop = i > 0 && stepPct < 60 && fromPrev >= 5;
                  return (
                    <div key={f.key} className="mb-3 last:mb-0" data-testid={`funnel-${f.key}`}>
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-slate-200">{f.label}</span>
                        <span className="font-bold text-white">
                          {f.count} <span className="text-slate-500 font-normal">({pct(f.count, total)}%)</span>
                        </span>
                      </div>
                      <div className="mt-1.5 h-2 rounded-full bg-white/5 overflow-hidden">
                        <div className={`h-full rounded-full ${isBigDrop ? "bg-footy-red" : "bg-footy-red/70"}`} style={{ width: `${width}%` }} />
                      </div>
                      {i > 0 && (
                        <div className="flex items-center gap-1.5 mt-1 text-[10px] uppercase tracking-widest">
                          {dropped > 0 ? (
                            <><TrendingDown size={10} className={isBigDrop ? "text-footy-red" : "text-slate-500"} />
                              <span className={isBigDrop ? "text-footy-red" : "text-slate-500"}>
                                −{dropped} dropped · {stepPct}% continued
                              </span>
                            </>
                          ) : (
                            <span className="text-emerald-400/70">no drop</span>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>

            {/* Engagement grid */}
            <section className="mt-8" data-testid="analytics-engagement">
              <div className="flex items-center gap-2 mb-3">
                <Heart size={16} className="text-footy-red" />
                <h2 className="font-display text-2xl uppercase leading-none">Engagement</h2>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <Stat label="Users who swiped" value={engagement.swipers} icon={Heart}
                  sub={`${engagement.likes ?? 0} likes · ${engagement.passes ?? 0} passes`} />
                <Stat label="Total swipes" value={engagement.total_swipes} icon={Heart} />
                <Stat label="Matches created" value={engagement.matches_total} icon={Heart} tone="emerald"
                  sub={`${engagement.distinct_matched_users ?? 0} unique users`} />
                <Stat label="Users who messaged" value={engagement.message_senders} icon={MessageCircle} />
                <Stat label="Messages sent" value={engagement.messages_total} icon={MessageCircle} />
                <Stat label="Fixtures posted" value={engagement.fixtures_created} icon={Calendar} />
                <Stat label="DOBs set" value={engagement.dob_set} icon={Cake} />
                <Stat label="Postcodes set" value={engagement.postcode_set} icon={MapPin} />
              </div>
            </section>

            {/* Connection type popularity */}
            <section className="mt-8" data-testid="analytics-connection-types">
              <div className="flex items-center gap-2 mb-3">
                <Users size={16} className="text-footy-red" />
                <h2 className="font-display text-2xl uppercase leading-none">"Looking for" popularity</h2>
              </div>
              <p className="text-xs text-slate-500 mb-4">
                Users can pick more than one, so totals will exceed profile count. Points you at which corners of the app to double down on.
              </p>
              <div className="footy-card p-5">
                {conn.length === 0 ? (
                  <p className="text-sm text-slate-400">Not enough data yet — waiting for signups to complete their profile.</p>
                ) : (() => {
                  const max = Math.max(...conn.map(c => c.count));
                  return conn.map(c => (
                    <div key={c.type} className="mb-3 last:mb-0" data-testid={`connection-type-${c.type}`}>
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-slate-200 font-semibold">{labelForConnection(c.type)}</span>
                        <span className="font-bold text-white">{c.count}</span>
                      </div>
                      <div className="mt-1.5 h-2 rounded-full bg-white/5 overflow-hidden">
                        <div className="h-full rounded-full bg-emerald-400/70" style={{ width: `${max > 0 ? (c.count / max) * 100 : 0}%` }} />
                      </div>
                    </div>
                  ));
                })()}
              </div>
            </section>

            {/* Top clubs */}
            {clubs.length > 0 && (
              <section className="mt-8" data-testid="analytics-top-clubs">
                <div className="flex items-center gap-2 mb-3">
                  <Trophy size={16} className="text-footy-red" />
                  <h2 className="font-display text-2xl uppercase leading-none">Top clubs</h2>
                </div>
                <div className="footy-card p-5">
                  {(() => {
                    const max = Math.max(...clubs.map(c => c.count));
                    return clubs.map((c, i) => (
                      <div key={c.club} className="mb-3 last:mb-0" data-testid={`top-club-${i}`}>
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-slate-200">
                            <span className="text-slate-500 font-mono mr-2">#{i + 1}</span>
                            {c.club}
                          </span>
                          <span className="font-bold text-white">{c.count}</span>
                        </div>
                        <div className="mt-1.5 h-2 rounded-full bg-white/5 overflow-hidden">
                          <div className="h-full rounded-full bg-footy-red/70" style={{ width: `${max > 0 ? (c.count / max) * 100 : 0}%` }} />
                        </div>
                      </div>
                    ));
                  })()}
                </div>
              </section>
            )}

            {/* Notification nudge funnel */}
            <section className="mt-8" data-testid="analytics-nudge-funnel">
              <div className="flex items-center gap-2 mb-3">
                <Bell size={16} className="text-footy-red" />
                <h2 className="font-display text-2xl uppercase leading-none">Notification nudge</h2>
              </div>
              <p className="text-xs text-slate-500 uppercase tracking-widest mb-3">Shown → subscribed conversion per placement</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {[
                  { key: "matches", label: "Matches page" },
                  { key: "chat", label: "After first chat message" },
                  { key: "fixtures", label: "Matchday (live window)" },
                ].map(p => {
                  const s = nudge[p.key] || { shown: 0, subscribed: 0, dismissed: 0 };
                  const rate = s.shown > 0 ? Math.round((s.subscribed / s.shown) * 100) : 0;
                  const dismissRate = s.shown > 0 ? Math.round((s.dismissed / s.shown) * 100) : 0;
                  return (
                    <div key={p.key} className="footy-card p-4" data-testid={`nudge-source-${p.key}`}>
                      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{p.label}</div>
                      <div className="mt-2 flex items-baseline gap-2">
                        <span className="font-display text-4xl leading-none text-white">{rate}%</span>
                        <span className="text-[10px] uppercase tracking-widest text-emerald-400 font-bold">Subscribed</span>
                      </div>
                      <div className="mt-3 h-2 rounded-full bg-white/5 overflow-hidden">
                        <div className="h-full rounded-full bg-emerald-500/70" style={{ width: `${rate}%` }} />
                      </div>
                      <div className="mt-3 flex items-center justify-between text-[11px] text-slate-400">
                        <span>Shown <span className="text-white font-semibold">{s.shown}</span></span>
                        <span>Subscribed <span className="text-emerald-300 font-semibold">{s.subscribed}</span></span>
                        <span>Dismissed <span className="text-slate-300 font-semibold">{s.dismissed}</span> <span className="text-slate-500">({dismissRate}%)</span></span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          </>
        )}
      </div>

      {confirmReset && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
             onClick={()=>setConfirmReset(false)} data-testid="analytics-reset-modal">
          <div className="w-full max-w-sm bg-footy-card border border-white/10 rounded-2xl p-6"
               onClick={(e)=>e.stopPropagation()}>
            <div className="flex items-center gap-3">
              <RotateCcw className="text-footy-red" />
              <h2 className="font-display text-2xl uppercase">Reset baseline?</h2>
            </div>
            <p className="text-sm text-slate-400 mt-3">
              Analytics will show only data from <span className="text-white font-semibold">now onwards</span>. Existing users, matches, messages and feedback all remain in the database — they'll just be hidden from this dashboard until you click "Show all-time".
            </p>
            <p className="text-xs text-slate-500 mt-2">Handy for filtering out your own test activity before real beta users arrive.</p>
            <div className="mt-6 flex gap-3">
              <button onClick={()=>setConfirmReset(false)} className="footy-btn-secondary flex-1"
                data-testid="analytics-reset-cancel">Cancel</button>
              <button onClick={()=>setBaseline(false)} disabled={resetting}
                className="footy-btn-primary flex-1" data-testid="analytics-reset-confirm">
                {resetting ? "Resetting..." : "Reset now"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, sub, icon: Icon, tone = "default" }) {
  const toneClass = tone === "emerald" ? "text-emerald-400" : "text-white";
  return (
    <div className="footy-card p-4">
      <div className="flex items-center gap-2 text-slate-500">
        {Icon && <Icon size={12} />}
        <p className="text-[10px] uppercase tracking-widest font-bold">{label}</p>
      </div>
      <p className={`font-display text-3xl leading-none mt-1 ${toneClass}`}>{value ?? 0}</p>
      {sub && <p className="text-[10px] text-slate-500 uppercase tracking-widest mt-1">{sub}</p>}
    </div>
  );
}
